import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { PortfolioService } from './portfolio.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '../../shared/services/common.service';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import {
  PortalSortDirection,
  PortalTableColumn,
  PortalTableSortChange,
} from 'src/app/shared/components/portal-table/portal-table.model';
import {
  PortfolioAttentionItemApi,
  PortfolioCompaniesOverviewApi,
  PortfolioCompanySummaryApi,
  PortfolioCompanyTableRow,
  PortfolioCompanyDrawerDetails,
  PortfolioFairValuePointApi,
  PortfolioPeriodType,
  LegacyPortfolioCompanyPayload,
  PortfolioReportingStatus,
  PortfolioTrendPointApi,
} from './portfolio.types';
import { environment } from 'src/environments/environment';
import { formatMoney, normalizeCurrencyCode } from './portfolio-money.utils';

interface PortfolioValueStep {
  label: string;
  value: string;
  detail: string;
  percent: number;
  tone?: 'positive' | 'negative' | 'neutral';
}

interface PortfolioReportingBreakdownItem {
  status: PortfolioReportingStatus;
  label: string;
  count: number;
  percent: number;
}

interface PortfolioWatchlistItem {
  companyId: string;
  companyName: string;
  value: string;
  detail: string;
  severity: 'critical' | 'warning' | 'info';
}

interface PortfolioPerformanceSignal {
  label: string;
  companyId?: string;
  companyName: string;
  value: string;
  detail: string;
  tone?: 'positive' | 'negative' | 'warning' | 'neutral';
}

type PortfolioOverviewSection =
  | 'valueCreation'
  | 'reportingHealth'
  | 'runwayWatchlist'
  | 'performanceSignals';

type PortfolioTrendMode = 'comparable' | 'all_reported';

@Component({
  selector: 'app-portfolio',
  templateUrl: './portfolio.component.html',
  styleUrls: ['./portfolio.component.scss'],
})
export class PortfolioComponent implements OnInit, OnDestroy {
  readonly showPortfolioManagementFlow = environment.enablePortfolioCompanyManagementFlow;
  readonly reportingStatusOrder: PortfolioReportingStatus[] = [
    'complete',
    'partial',
    'missing_data',
    'not_configured',
  ];
  collapsedOverviewSections: Record<PortfolioOverviewSection, boolean> = {
    valueCreation: true,
    reportingHealth: true,
    runwayWatchlist: true,
    performanceSignals: true,
  };

  readonly chartWidth = 1400;
  readonly chartHeight = 360;
  readonly chartPadding = {
    top: 26,
    right: 34,
    bottom: 52,
    left: 96,
  };
  readonly chartPlotPadding = {
    top: 12,
    right: 24,
    bottom: 12,
    left: 24,
  };

  search = '';
  vehicleId = '';
  gpId = '';
  portfolioOverview: PortfolioCompaniesOverviewApi | null = null;
  selectedCurrency = 'EUR';
  selectedTrendType: PortfolioPeriodType | null = null;
  trendMode: PortfolioTrendMode = 'comparable';
  portfolioData: PortfolioCompanyTableRow[] = [];
  tempPortfolioData: PortfolioCompanyTableRow[] = [];
  noPortfolioFound = false;
  isLoading = false;
  pageIndex = 0;
  pageSize = 10;
  readonly pageSizeOptions = [10, 25, 50];
  sortActive = 'company';
  portalSortDirection: PortalSortDirection = 'asc';
  private readonly destroy$ = new Subject<void>();

  portfolioColumns: PortalTableColumn[] = [
    {
      key: 'company',
      label: 'Company',
      width: 'minmax(270px, 1.35fr)',
      sortable: true,
    },
    {
      key: 'latestPeriodLabel',
      label: 'Latest period',
      width: 'minmax(130px, 0.6fr)',
      sortable: true,
    },
    {
      key: 'latestRevenue',
      label: 'Revenue',
      width: 'minmax(130px, 0.65fr)',
      sortable: true,
    },
    {
      key: 'latestEbitda',
      label: 'EBITDA',
      width: 'minmax(130px, 0.65fr)',
      sortable: true,
    },
    {
      key: 'fairValueOfHolding',
      label: 'FV holding',
      width: 'minmax(140px, 0.7fr)',
      sortable: true,
    },
    {
      key: 'moic',
      label: 'MOIC',
      width: 'minmax(110px, 0.5fr)',
      sortable: true,
    },
    {
      key: 'reportingStatus',
      label: 'Reporting',
      width: 'minmax(170px, 0.75fr)',
      sortable: true,
    },
  ];

  constructor(
    private portfolioService: PortfolioService,
    private activatedRoute: ActivatedRoute,
    public commonService: CommonService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    this.vehicleId = this.activatedRoute.snapshot.params['vehicleId'];
    this.gpId = this.activatedRoute.snapshot.params['gpId'];
    this.commonService.setSecondaryPath([]);
    this.getPortfolioList();
    this.portfolioService.refreshPortfolioListObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response) {
          this.portfolioService.setRefreshPortfolio(false);
          setTimeout(() => {
            this.getPortfolioList();
          }, 100);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  getPortfolioList() {
    this.isLoading = true;
    this.noPortfolioFound = false;
    this.portfolioService.getPortfolioCompanyOverview(this.vehicleId).subscribe({
      next: response => {
        this.portfolioOverview = response;
        this.initializeTrendContext();
        const items = (response.companies ?? []).map(item => this.mapCompanySummaryToPortfolio(item));
        this.noPortfolioFound = items.length === 0;
        this.portfolioData = items;
        this.tempPortfolioData = items;
        this.applyFilter();
      },
      error: () => {
        this.isLoading = false;
        this.commonService.errorNotification(
          'Failed to retrieve Portfolio Data'
        );
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  setTrendType(type: PortfolioPeriodType): void {
    this.selectedTrendType = type;
    this.trendMode = this.hasComparableTrend ? 'comparable' : 'all_reported';
  }

  setCurrency(currency: string): void {
    const normalized = normalizeCurrencyCode(currency);
    if (!normalized || normalized === this.selectedCurrency) {
      return;
    }
    this.selectedCurrency = normalized;
    const availableTypes = this.trendTypes;
    if (!this.selectedTrendType || !availableTypes.includes(this.selectedTrendType)) {
      this.selectedTrendType = this.latestCurrencyTrendPoint?.type ?? availableTypes[0] ?? null;
    }
    this.trendMode = this.hasComparableTrend ? 'comparable' : 'all_reported';
  }

  setTrendMode(mode: PortfolioTrendMode): void {
    if (mode === 'comparable' && !this.hasComparableTrend) {
      return;
    }
    this.trendMode = mode;
  }

  trendTypeLabel(type: PortfolioPeriodType | null): string {
    switch (type) {
      case 'quarter':
        return 'Quarterly';
      case 'fiscal_year':
        return 'Fiscal year';
      case 'projection':
        return 'Projection';
      default:
        return 'Reporting period';
    }
  }

  trendPointCoverage(point: PortfolioTrendPointApi): string {
    const total = this.tempPortfolioData
      .filter(company => normalizeCurrencyCode(company.currency) === this.selectedCurrency).length;
    if (this.trendMode === 'comparable') {
      return `Cohort ${point.comparableCompanies}/${total}`;
    }
    if (point.revenueCompaniesReported === point.ebitdaCompaniesReported) {
      return `${point.companiesReported}/${total} reporting`;
    }
    return `Revenue ${point.revenueCompaniesReported}/${total} · EBITDA ${point.ebitdaCompaniesReported}/${total}`;
  }

  private initializeTrendContext(): void {
    const trend = this.portfolioOverview?.trend ?? [];
    const latestPoint = [...trend]
      .sort((first, second) => first.endDate.localeCompare(second.endDate))
      .at(-1);
    const summaryCurrency = normalizeCurrencyCode(this.portfolioOverview?.summary.portfolioCurrency);
    this.selectedCurrency = summaryCurrency || normalizeCurrencyCode(latestPoint?.currency)
      || this.availableCurrencies[0] || 'EUR';
    const currencyTrend = trend.filter(point => normalizeCurrencyCode(point.currency) === this.selectedCurrency);
    const availableTypes = new Set(currencyTrend.map(point => point.type));
    const currencyLatestPoint = [...currencyTrend]
      .sort((first, second) => first.endDate.localeCompare(second.endDate))
      .at(-1);

    this.selectedTrendType = currencyLatestPoint && availableTypes.has(currencyLatestPoint.type)
      ? currencyLatestPoint.type
      : null;
    this.trendMode = this.hasComparableTrend ? 'comparable' : 'all_reported';
  }

  private mapCompanySummaryToPortfolio(item: PortfolioCompanySummaryApi): PortfolioCompanyTableRow {
    return {
      id: item.id,
      name: item.name,
      legalName: item.legalName ?? '',
      domicile: item.domicile ?? '',
      currency: item.currency ?? '',
      website: item.website ?? '',
      updatedAt: item.updatedAt ?? '',
      createdAt: item.createdAt ?? '',
      logo: item.logoUrl ?? '',
      inPortfolioSince: '',
      type: 'portfolioCompany',
      latestPeriodLabel: item.latestPeriodLabel ?? '',
      reportingStatus: item.reportingStatus,
      reportingCompletion: item.reportingCompletion,
      latestRevenue: item.latestRevenue,
      latestEbitda: item.latestEbitda,
      latestCash: item.latestCash,
      runwayMonths: item.runwayMonths,
      fairValueOfHolding: item.fairValueOfHolding,
      historicalCost: item.historicalCost,
      moic: item.moic,
      grossIrr: item.grossIrr,
    };
  }

  openManagement() {
    this.router.navigate(['manage'], { relativeTo: this.activatedRoute });
  }

  editPortfolio(data: PortfolioCompanyTableRow): void {
    this.portfolioService.openPortfolioDrawer();
    this.portfolioService.setPortfolioDetails({
      ...data,
      isEdit: true,
      vehicleId: this.vehicleId,
    });
    this.portfolioService.setRefreshPortfolio(false);
  }

  navigateToPortfolioCompany(data: PortfolioCompanyTableRow): void {
    this.router.navigate([this.getCompanySlug(data)], { relativeTo: this.activatedRoute });
  }

  navigateToPortfolioCompanyById(companyId: string) {
    const company = this.tempPortfolioData.find(item => item.id === companyId);
    this.router.navigate([company ? this.getCompanySlug(company) : companyId], { relativeTo: this.activatedRoute });
  }

  getCompanySlug(data: PortfolioCompanyTableRow): string {
    if (data.id) {
      return data.id;
    }

    return (data.name || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '') || 'company';
  }

  applyFilter() {
    const serachText = this.search ? this.search.toLowerCase() : '';
    this.portfolioData = this.tempPortfolioData.filter(item => {
      const searchableText = [
        item.name,
        item.legalName,
        item.domicile,
        item.currency,
        item.website,
        item.latestPeriodLabel,
        item.reportingStatus ? this.reportingStatusLabel(item.reportingStatus) : '',
        this.portfolioTypeLabel(item.type),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(serachText);
    });
    this.pageIndex = 0;
  }

  clearFilter() {
    this.search = '';
    this.applyFilter();
  }

  tableRows(): PortfolioCompanyTableRow[] {
    const start = this.pageIndex * this.pageSize;
    return this.sortedPortfolioRows().slice(start, start + this.pageSize);
  }

  totalRows(): number {
    return this.portfolioData.length;
  }

  setPageIndex(index: number) {
    this.pageIndex = index;
  }

  setRowsPerPage(size: number) {
    this.pageSize = size;
    this.pageIndex = 0;
  }

  portalSortChange(sort: PortalTableSortChange) {
    this.sortActive = sort.active || 'company';
    this.portalSortDirection = sort.direction || 'asc';
    this.pageIndex = 0;
  }

  portfolioTypeLabel(type?: string): string {
    if (!type) {
      return '-';
    }

    return type === 'portfolioCompany' ? 'Portfolio company' : type;
  }

  companyMeta(row: PortfolioCompanyTableRow): string {
    return [row.legalName, row.domicile, row.currency]
      .filter(value => !!value)
      .join(' / ');
  }

  get trendTypes(): PortfolioPeriodType[] {
    const availableTypes = new Set((this.portfolioOverview?.trend ?? [])
      .filter(point => normalizeCurrencyCode(point.currency) === this.selectedCurrency)
      .map(point => point.type));
    return (['quarter', 'fiscal_year', 'projection'] as PortfolioPeriodType[])
      .filter(type => availableTypes.has(type));
  }

  get selectedRawTrendData(): PortfolioTrendPointApi[] {
    if (!this.selectedTrendType) {
      return [];
    }

    return (this.portfolioOverview?.trend ?? [])
      .filter(point => point.type === this.selectedTrendType && normalizeCurrencyCode(point.currency) === this.selectedCurrency)
      .sort((first, second) => first.startDate.localeCompare(second.startDate));
  }

  get trendData(): PortfolioTrendPointApi[] {
    return this.selectedRawTrendData.map(point => this.trendMode === 'comparable'
      ? {
        ...point,
        revenue: point.comparableRevenue,
        ebitda: point.comparableEbitda,
        companiesReported: point.comparableCompanies,
      }
      : point);
  }

  get hasComparableTrend(): boolean {
    return this.selectedRawTrendData.length > 0
      && this.selectedRawTrendData.every(point => point.comparableCompanies > 0);
  }

  get latestTrendPoint(): PortfolioTrendPointApi | undefined {
    return this.trendData[this.trendData.length - 1];
  }

  get latestRawTrendPoint(): PortfolioTrendPointApi | undefined {
    return this.selectedRawTrendData[this.selectedRawTrendData.length - 1];
  }

  get latestPeriodLabel(): string {
    return this.latestTrendPoint?.periodLabel ?? this.portfolioOverview?.summary.latestPeriodLabel ?? '';
  }

  get latestRevenue(): number | null {
    return this.latestTrendPoint?.revenue ?? null;
  }

  get latestEbitda(): number | null {
    return this.latestTrendPoint?.ebitda ?? null;
  }

  get trendContextDescription(): string {
    const period = this.trendTypeLabel(this.selectedTrendType).toLowerCase();
    const cohort = this.trendMode === 'comparable'
      ? 'same companies in every period'
      : 'all reported companies per period';
    return `Aggregated ${period} data · ${cohort}.`;
  }

  get latestTrendCoverageLabel(): string {
    const point = this.latestRawTrendPoint;
    if (!point) {
      return 'No reporting data';
    }
    return this.trendPointCoverage(point);
  }

  get fairValueRows(): PortfolioFairValuePointApi[] {
    return this.selectedFairValueRows.slice(0, 6);
  }

  get fairValueCoverageLabel(): string {
    const reported = this.selectedFairValueRows.length;
    const total = this.tempPortfolioData.filter(company => normalizeCurrencyCode(company.currency) === this.selectedCurrency).length;
    return `${reported}/${total} ${this.selectedCurrency} companies with FV`;
  }

  get visibleAttentionItems(): PortfolioAttentionItemApi[] {
    return this.portfolioOverview?.attentionItems ?? [];
  }

  get primaryCurrency(): string {
    return this.selectedCurrency;
  }

  get availableCurrencies(): string[] {
    return Array.from(new Set([
      ...(this.portfolioOverview?.trend ?? []).map(point => normalizeCurrencyCode(point.currency)),
      ...(this.portfolioOverview?.fairValueByCompany ?? []).map(point => normalizeCurrencyCode(point.currency)),
      ...(this.portfolioOverview?.companies ?? []).map(company => normalizeCurrencyCode(company.currency)),
    ].filter(Boolean))).sort();
  }

  get currencyContextLabel(): string {
    return this.availableCurrencies.length > 1
      ? `${this.selectedCurrency} view · ${this.availableCurrencies.length} currencies in portfolio`
      : this.selectedCurrency;
  }

  get latestCurrencyTrendPoint(): PortfolioTrendPointApi | undefined {
    return [...(this.portfolioOverview?.trend ?? [])]
      .filter(point => normalizeCurrencyCode(point.currency) === this.selectedCurrency)
      .sort((first, second) => first.endDate.localeCompare(second.endDate))
      .at(-1);
  }

  get selectedFairValueRows(): PortfolioFairValuePointApi[] {
    return (this.portfolioOverview?.fairValueByCompany ?? [])
      .filter(item => normalizeCurrencyCode(item.currency) === this.selectedCurrency);
  }

  get selectedHistoricalCostTotal(): number | null {
    const values = this.selectedFairValueRows
      .map(item => item.historicalCost)
      .filter((value): value is number => value != null);
    return values.length ? values.reduce((total, value) => total + value, 0) : null;
  }

  get selectedImpliedMoic(): number | null {
    const cost = this.selectedHistoricalCostTotal;
    const fairValue = this.fairValueTotal;
    return cost != null && cost !== 0 && fairValue != null ? fairValue / cost : null;
  }

  get reportingCoverageLabel(): string {
    const summary = this.portfolioOverview?.summary;
    if (!summary) {
      return '-';
    }

    return `${summary.reportingCompleteCompanies}/${summary.companyCount}`;
  }

  get portfolioGainLoss(): number | null {
    const historicalCost = this.selectedHistoricalCostTotal;
    if (historicalCost == null || this.fairValueTotal == null) {
      return null;
    }

    return this.fairValueTotal - historicalCost;
  }

  get valueCreationSteps(): PortfolioValueStep[] {
    const historicalCost = this.selectedHistoricalCostTotal;
    const fairValue = this.fairValueTotal;
    const gainLoss = this.portfolioGainLoss;
    const maxValue = Math.max(
      Math.abs(historicalCost ?? 0),
      Math.abs(fairValue ?? 0),
      Math.abs(gainLoss ?? 0),
      1
    );

    return [
      {
        label: 'Historical cost',
        value: this.formatCurrencyValue(historicalCost),
        detail: 'Invested cost base',
        percent: this.valueStepPercent(historicalCost, maxValue),
        tone: 'neutral',
      },
      {
        label: 'Fair value',
        value: this.formatCurrencyValue(fairValue),
        detail: `Implied MOIC ${this.formatMoic(this.selectedImpliedMoic)}`,
        percent: this.valueStepPercent(fairValue, maxValue),
        tone: 'positive',
      },
      {
        label: 'Gain / loss',
        value: this.formatCurrencyValue(gainLoss),
        detail: gainLoss == null ? 'No cost and FV pair yet' : 'Unrealized value movement',
        percent: this.valueStepPercent(gainLoss, maxValue),
        tone: gainLoss == null || gainLoss === 0 ? 'neutral' : gainLoss > 0 ? 'positive' : 'negative',
      },
    ];
  }

  get fairValueTotal(): number | null {
    const values = this.selectedFairValueRows
      .map(item => item.value)
      .filter((value): value is number => value != null);
    return values.length
      ? values.reduce((total, value) => total + Math.max(value, 0), 0)
      : null;
  }

  get topOneExposurePercent(): number | null {
    const values = this.sortedFairValueValues();
    return this.exposurePercent(values.slice(0, 1));
  }

  get topThreeExposurePercent(): number | null {
    const values = this.sortedFairValueValues();
    return this.exposurePercent(values.slice(0, 3));
  }

  get reportingStatusBreakdown(): PortfolioReportingBreakdownItem[] {
    const total = this.tempPortfolioData.length || 1;

    return this.reportingStatusOrder.map(status => {
      const count = this.tempPortfolioData.filter(company => company.reportingStatus === status).length;

      return {
        status,
        label: this.reportingStatusLabel(status),
        count,
        percent: (count / total) * 100,
      };
    });
  }

  get runwayWatchlist(): PortfolioWatchlistItem[] {
    const items = this.tempPortfolioData
      .map(company => this.watchlistItem(company))
      .filter((item): item is PortfolioWatchlistItem => item !== null);
    const severityRank: Record<PortfolioWatchlistItem['severity'], number> = {
      critical: 0,
      warning: 1,
      info: 2,
    };

    return items
      .sort((first, second) => severityRank[first.severity] - severityRank[second.severity])
      .slice(0, 6);
  }

  get performanceSignals(): PortfolioPerformanceSignal[] {
    const companies = this.tempPortfolioData
      .filter(company => normalizeCurrencyCode(company.currency) === this.selectedCurrency);
    const signals: PortfolioPerformanceSignal[] = [];
    const topRevenue = this.maxBy(companies, company => company.latestRevenue);
    const bestMoic = this.maxBy(companies, company => company.moic);
    const lowestEbitda = this.minBy(companies, company => company.latestEbitda);
    const valueMovement = companies
      .map(company => ({
        company,
        gainLoss: company.fairValueOfHolding != null && company.historicalCost != null
          ? company.fairValueOfHolding - company.historicalCost
          : null,
      }))
      .filter((item): item is { company: PortfolioCompanyTableRow; gainLoss: number } => item.gainLoss != null)
      .sort((first, second) => {
        const firstNegative = first.gainLoss < 0;
        const secondNegative = second.gainLoss < 0;
        if (firstNegative !== secondNegative) {
          return firstNegative ? -1 : 1;
        }
        return Math.abs(second.gainLoss) - Math.abs(first.gainLoss);
      })[0];

    if (topRevenue) {
      signals.push({
        label: 'Top revenue',
        companyId: topRevenue.id,
        companyName: topRevenue.name,
        value: this.formatCurrencyValue(topRevenue.latestRevenue, topRevenue.currency || this.primaryCurrency),
        detail: `${topRevenue.latestPeriodLabel || 'Latest'} reported revenue`,
        tone: 'positive',
      });
    }

    if (bestMoic) {
      signals.push({
        label: 'Best MOIC',
        companyId: bestMoic.id,
        companyName: bestMoic.name,
        value: this.formatMoic(bestMoic.moic),
        detail: 'Highest implied multiple',
        tone: 'positive',
      });
    }

    if (lowestEbitda && lowestEbitda.latestEbitda != null) {
      signals.push({
        label: lowestEbitda.latestEbitda < 0 ? 'EBITDA pressure' : 'Lowest EBITDA',
        companyId: lowestEbitda.id,
        companyName: lowestEbitda.name,
        value: this.formatCurrencyValue(lowestEbitda.latestEbitda, lowestEbitda.currency || this.primaryCurrency),
        detail: `${lowestEbitda.latestPeriodLabel || 'Latest'} EBITDA`,
        tone: lowestEbitda.latestEbitda < 0 ? 'negative' : 'neutral',
      });
    }

    if (valueMovement) {
      signals.push({
        label: valueMovement.gainLoss < 0 ? 'FV below cost' : 'Value uplift',
        companyId: valueMovement.company.id,
        companyName: valueMovement.company.name,
        value: this.formatCurrencyValue(valueMovement.gainLoss, valueMovement.company.currency || this.primaryCurrency),
        detail: `${valueMovement.company.latestPeriodLabel || 'Latest'} · ${
          valueMovement.gainLoss < 0 ? 'below historical cost' : 'above historical cost'
        }`,
        tone: valueMovement.gainLoss < 0 ? 'warning' : 'positive',
      });
    }

    return signals;
  }

  get chartInnerWidth(): number {
    return this.chartWidth - this.chartPadding.left - this.chartPadding.right;
  }

  get chartInnerHeight(): number {
    return this.chartHeight - this.chartPadding.top - this.chartPadding.bottom;
  }

  get trendChartMax(): number {
    const values = this.trendData.flatMap(point => [
      point.revenue ?? 0,
      point.ebitda ?? 0,
    ]);
    const max = Math.max(...values, 0);
    const min = Math.min(...values, 0);

    return max === min ? max + 1 : max;
  }

  get trendChartMin(): number {
    const values = this.trendData.flatMap(point => [
      point.revenue ?? 0,
      point.ebitda ?? 0,
    ]);
    const max = Math.max(...values, 0);
    const min = Math.min(...values, 0);

    return max === min ? min - 1 : min;
  }

  get trendChartRange(): number {
    return this.trendChartMax - this.trendChartMin || 1;
  }

  get chartGridValues(): number[] {
    const max = this.trendChartMax;
    const min = this.trendChartMin;

    return [max, min + this.trendChartRange * 0.75, min + this.trendChartRange * 0.5, min + this.trendChartRange * 0.25, min];
  }

  trendPath(series: 'revenue' | 'ebitda'): string {
    let hasPreviousPoint = false;
    return this.trendData.map((point, index) => {
      const value = point[series];
      if (value == null) {
        hasPreviousPoint = false;
        return '';
      }

      const command = hasPreviousPoint ? 'L' : 'M';
      hasPreviousPoint = true;
      return `${command} ${this.trendX(index)},${this.trendY(value)}`;
    }).filter(Boolean).join(' ');
  }

  trendCircles(series: 'revenue' | 'ebitda'): { x: number; y: number; value: number; label: string }[] {
    return this.trendData
      .map((point, index) => {
        const value = point[series];
        if (value == null) {
          return null;
        }

        return {
          x: this.trendX(index),
          y: this.trendY(value),
          value,
          label: point.periodLabel,
        };
      })
      .filter((point): point is { x: number; y: number; value: number; label: string } => point !== null);
  }

  trendX(index: number): number {
    if (this.trendData.length <= 1) {
      return this.chartPadding.left + this.chartInnerWidth / 2;
    }

    return this.chartPadding.left + (index / (this.trendData.length - 1)) * this.chartInnerWidth;
  }

  trendY(value: number): number {
    return this.chartPadding.top + this.chartInnerHeight -
      ((value - this.trendChartMin) / this.trendChartRange) * this.chartInnerHeight;
  }

  trendPlotPath(series: 'revenue' | 'ebitda'): string {
    let hasPreviousPoint = false;
    return this.trendData.map((point, index) => {
      const value = point[series];
      if (value == null) {
        hasPreviousPoint = false;
        return '';
      }

      const command = hasPreviousPoint ? 'L' : 'M';
      hasPreviousPoint = true;
      return `${command} ${this.trendPlotX(index)},${this.trendPlotY(value)}`;
    }).filter(Boolean).join(' ');
  }

  trendPlotPoints(series: 'revenue' | 'ebitda'): { x: number; y: number; value: number; label: string }[] {
    return this.trendData
      .map((point, index) => {
        const value = point[series];
        if (value == null) {
          return null;
        }

        return {
          x: this.trendPlotX(index),
          y: this.trendPlotY(value),
          value,
          label: point.periodLabel,
        };
      })
      .filter((point): point is { x: number; y: number; value: number; label: string } => point !== null);
  }

  trendPlotX(index: number): number {
    const plotWidth = this.chartInnerWidth - this.chartPlotPadding.left - this.chartPlotPadding.right;
    if (this.trendData.length <= 1) {
      return this.chartPlotPadding.left + plotWidth / 2;
    }

    return this.chartPlotPadding.left + (index / (this.trendData.length - 1)) * plotWidth;
  }

  trendPlotY(value: number): number {
    const plotHeight = this.chartInnerHeight - this.chartPlotPadding.top - this.chartPlotPadding.bottom;
    return this.chartPlotPadding.top + plotHeight -
      ((value - this.trendChartMin) / this.trendChartRange) * plotHeight;
  }

  trendTickOffset(value: number): number {
    return (this.trendPlotY(value) / this.chartInnerHeight) * 100;
  }

  trendLabelOffset(index: number): number {
    return (this.trendPlotX(index) / this.chartInnerWidth) * 100;
  }

  fairValuePercent(item: PortfolioFairValuePointApi): number {
    const max = Math.max(...this.fairValueRows.map(row => row.value ?? 0), 0);
    if (!item.value || max === 0) {
      return 0;
    }

    return Math.max(6, (item.value / max) * 100);
  }

  fairValueShare(item: PortfolioFairValuePointApi): number | null {
    const total = this.fairValueTotal;
    if (!item.value || total == null || total <= 0) {
      return null;
    }

    return (item.value / total) * 100;
  }

  valueStepClass(step: PortfolioValueStep): string {
    return step.tone ? `portfolio-value-step--${step.tone}` : '';
  }

  reportingBreakdownClass(item: PortfolioReportingBreakdownItem): string {
    return `portfolio-health-segment--${item.status.replace('_', '-')}`;
  }

  watchlistSeverityClass(item: PortfolioWatchlistItem): string {
    return `portfolio-watchlist-item--${item.severity}`;
  }

  performanceSignalClass(item: PortfolioPerformanceSignal): string {
    return item.tone ? `portfolio-performance-card--${item.tone}` : '';
  }

  isOverviewSectionCollapsed(section: PortfolioOverviewSection): boolean {
    return this.collapsedOverviewSections[section];
  }

  toggleOverviewSection(section: PortfolioOverviewSection): void {
    this.collapsedOverviewSections[section] = !this.collapsedOverviewSections[section];
  }

  formatCurrencyValue(
    value: number | null | undefined,
    currency: string = this.primaryCurrency,
    compact = false
  ): string {
    if (value == null) {
      return '-';
    }

    try {
      return formatMoney(value, currency && currency !== 'Mixed' ? currency : this.primaryCurrency, compact);
    } catch {
      return formatMoney(value, this.primaryCurrency, compact);
    }
  }

  formatMoic(value: number | null | undefined): string {
    if (value == null) {
      return '-';
    }

    return `${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: value % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 2,
    }).format(value)}x`;
  }

  formatNumberValue(value: number | null | undefined, suffix = ''): string {
    if (value == null) {
      return '-';
    }

    return `${new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 1,
    }).format(value)}${suffix}`;
  }

  formatPercentValue(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
      return '-';
    }

    return `${new Intl.NumberFormat('en-US', {
      maximumFractionDigits: value >= 10 ? 0 : 1,
    }).format(value)}%`;
  }

  reportingStatusLabel(status?: PortfolioReportingStatus): string {
    switch (status) {
      case 'complete':
        return 'Complete';
      case 'partial':
        return 'Partial';
      case 'missing_data':
        return 'Missing data';
      case 'not_configured':
        return 'Needs setup';
      default:
        return '-';
    }
  }

  reportingStatusClass(status?: PortfolioReportingStatus): string {
    return `portfolio-status--${(status || 'missing_data').replace('_', '-')}`;
  }

  attentionSeverityClass(item: PortfolioAttentionItemApi): string {
    return `portfolio-attention-item--${item.severity}`;
  }

  normalizeWebsiteUrl(website: string): string {
    const trimmedWebsite = website.trim();

    return /^https?:\/\//i.test(trimmedWebsite)
      ? trimmedWebsite
      : `https://${trimmedWebsite}`;
  }

  private valueStepPercent(value: number | null | undefined, maxValue: number): number {
    if (value == null || maxValue <= 0) {
      return 0;
    }

    return Math.min(100, Math.max(6, (Math.abs(value) / maxValue) * 100));
  }

  private sortedFairValueValues(): number[] {
    return this.selectedFairValueRows
      .map(item => item.value ?? 0)
      .filter(value => value > 0)
      .sort((first, second) => second - first);
  }

  private exposurePercent(values: number[]): number | null {
    const total = this.fairValueTotal;
    if (!values.length || total == null || total <= 0) {
      return null;
    }

    return (values.reduce((sum, value) => sum + value, 0) / total) * 100;
  }

  private watchlistItem(company: PortfolioCompanyTableRow): PortfolioWatchlistItem | null {
    const currency = company.currency || this.primaryCurrency;
    if (company.runwayMonths != null && company.runwayMonths <= 6) {
      return {
        companyId: company.id,
        companyName: company.name,
        value: `${this.formatNumberValue(company.runwayMonths)} mo`,
        detail: `${this.formatCurrencyValue(company.latestCash, currency)} cash in ${company.latestPeriodLabel || 'latest period'}`,
        severity: company.runwayMonths <= 3 ? 'critical' : 'warning',
      };
    }

    if (company.latestCash != null && company.latestCash < 0) {
      return {
        companyId: company.id,
        companyName: company.name,
        value: this.formatCurrencyValue(company.latestCash, currency),
        detail: `Negative cash in ${company.latestPeriodLabel || 'latest period'}`,
        severity: 'critical',
      };
    }

    if (company.latestCash != null && company.runwayMonths == null) {
      return {
        companyId: company.id,
        companyName: company.name,
        value: 'No runway',
        detail: `${this.formatCurrencyValue(company.latestCash, currency)} cash, runway missing`,
        severity: 'info',
      };
    }

    return null;
  }

  private maxBy(
    companies: PortfolioCompanyTableRow[],
    valueSelector: (company: PortfolioCompanyTableRow) => number | null | undefined
  ): PortfolioCompanyTableRow | null {
    return companies
      .filter(company => valueSelector(company) != null)
      .sort((first, second) => (valueSelector(second) ?? Number.NEGATIVE_INFINITY) - (valueSelector(first) ?? Number.NEGATIVE_INFINITY))[0] ?? null;
  }

  private minBy(
    companies: PortfolioCompanyTableRow[],
    valueSelector: (company: PortfolioCompanyTableRow) => number | null | undefined
  ): PortfolioCompanyTableRow | null {
    return companies
      .filter(company => valueSelector(company) != null)
      .sort((first, second) => (valueSelector(first) ?? Number.POSITIVE_INFINITY) - (valueSelector(second) ?? Number.POSITIVE_INFINITY))[0] ?? null;
  }

  private sortedPortfolioRows(): PortfolioCompanyTableRow[] {
    const direction = this.portalSortDirection === 'desc' ? -1 : 1;

    return [...this.portfolioData].sort((first, second) => {
      const firstValue = this.sortValue(first);
      const secondValue = this.sortValue(second);

      if (firstValue < secondValue) {
        return -1 * direction;
      }

      if (firstValue > secondValue) {
        return 1 * direction;
      }

      return 0;
    });
  }

  private sortValue(row: PortfolioCompanyTableRow): string | number {
    switch (this.sortActive) {
      case 'latestPeriodLabel':
        return row.latestPeriodLabel?.toLowerCase() || '';
      case 'latestRevenue':
        return row.latestRevenue ?? Number.NEGATIVE_INFINITY;
      case 'latestEbitda':
        return row.latestEbitda ?? Number.NEGATIVE_INFINITY;
      case 'fairValueOfHolding':
        return row.fairValueOfHolding ?? Number.NEGATIVE_INFINITY;
      case 'moic':
        return row.moic ?? Number.NEGATIVE_INFINITY;
      case 'reportingStatus':
        return row.reportingCompletion ?? 0;
      case 'legalName':
        return row.legalName?.toLowerCase() || '';
      case 'domicile':
        return row.domicile?.toLowerCase() || '';
      case 'currency':
        return row.currency?.toLowerCase() || '';
      case 'website':
        return row.website?.toLowerCase() || '';
      case 'updatedAt':
        return row.updatedAt ? new Date(row.updatedAt).getTime() : 0;
      case 'inPortfolioSince':
        return row.inPortfolioSince ? new Date(row.inPortfolioSince).getTime() : 0;
      case 'type':
        return this.portfolioTypeLabel(row.type).toLowerCase();
      case 'company':
      case 'name':
      default:
        return row.name?.toLowerCase() || '';
    }
  }
  deletePortfolio(portfolio: PortfolioCompanyTableRow): void {
    const dialogRef = this.dialog.open(DeletePortfolioDialogComponent, {
      data: {
        portfolio,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'success') {
        this.portfolioData = this.portfolioData.filter(
          _item => _item.id !== portfolio.id
        );
        this.tempPortfolioData = this.tempPortfolioData.filter(
          _item => _item.id !== portfolio.id
        );
        if (this.tempPortfolioData.length === 0) {
          this.noPortfolioFound = true;
          this.search = '';
        }
        const lastPage = Math.max(
          0,
          Math.ceil(this.totalRows() / this.pageSize) - 1
        );
        this.pageIndex = Math.min(this.pageIndex, lastPage);
        this.commonService.successNotification(
          'Portfolio company deleted successfully'
        );
      } else if (result === 'error') {
        this.commonService.errorNotification(
          'Failed to delete portfolio company'
        );
      }
    });
  }
}

@Component({
  selector: 'app-add-company',
  templateUrl: './add-company.component.html',
  styleUrls: ['./portfolio.component.scss'],
})
export class AddCompanyComponent implements OnInit, OnDestroy {
  addPortfolioForm!: UntypedFormGroup;
  fileDimensionError = false;
  fileValidationError = false;
  isFileSizeExceed = false;
  submitted = false;
  base64Image: string | null = null;
  vehicleId = '';
  isEdit = false;
  portfolioDetails: PortfolioCompanyDrawerDetails | null = null;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  private readonly destroy$ = new Subject<void>();
  constructor(
    public portfolioService: PortfolioService,
    public commonService: CommonService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.loadAddPortfolioForm();
    this.portfolioService.portfolioDetailsObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(details => {
        this.resetFileErrors();
        this.addPortfolioForm.reset();

        if (details?.isEdit) {
          this.isEdit = true;
          this.portfolioDetails = details;
          this.vehicleId = details.vehicleId ?? '';
          this.setPortfolioForm(details);
          return;
        }

        this.vehicleId = details?.vehicleId ?? '';
        this.base64Image = '';
        this.isEdit = false;
        this.portfolioDetails = null;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAddPortfolioForm(): void {
    this.addPortfolioForm = this.fb.group({
      name: ['', [Validators.required]],
      inPortfolioSince: ['', [Validators.required]],
    });
  }

  setPortfolioForm(details: PortfolioCompanyDrawerDetails): void {
    this.addPortfolioForm.setValue({
      name: details.name ?? '',
      inPortfolioSince: details.inPortfolioSince
        ? this.convertToCustomFormat(details.inPortfolioSince)
        : '',
    });
    this.base64Image = details.logo ?? null;
  }
  get name() {
    return this.addPortfolioForm.get('name');
  }
  get inPortfolioSince() {
    return this.addPortfolioForm.get('inPortfolioSince');
  }
  triggerFileInput(): void {
    this.fileInput?.nativeElement.click();
  }
  onFileSelected(event: Event, fileInput: HTMLInputElement): void {
    this.fileValidationError = false;
    this.fileDimensionError = false;
    this.isFileSizeExceed = false;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const validFileTypes = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/jpg'];
      if (!validFileTypes.includes(file.type)) {
        this.fileValidationError = true;
        fileInput.value = '';
        return;
      }
      /*
      // file size validation
      const maxSizeInBytes = 10 * 1024 * 1024;
      if (file.size >= maxSizeInBytes) {
        this.isFileSizeExceed = true;
        fileInput.value = '';
        return;
      }
      */
      this.convertToBase64(file, fileInput);
      // file dimensions validation
      // this.validateImageDimensions(file).then(isValid => {
      //   if (isValid) {
      //     this.convertToBase64(file, fileInput);
      //   } else {
      //     this.fileDimensionError = true;
      //     fileInput.value = '';
      //   }
      // });
    }
  }
  validateImageDimensions(file: File): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // skip the condition for svg image
      if (file.type === 'image/svg+xml') {
        resolve(true);
        return;
      }
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        const result = event.target?.result;
        if (typeof result !== 'string') {
          reject(new Error('Unable to read the image for validation.'));
          return;
        }
        const img = new Image();
        img.onload = () => {
          if (img.width >= 200 && img.height >= 200) {
            resolve(true);
          } else {
            resolve(false);
          }
        };
        img.onerror = () => {
          reject('Unable to load the image for validation.');
        };
        img.src = result;
      };
      reader.readAsDataURL(file);
    });
  }

  convertToBase64(file: File, fileInput: HTMLInputElement): void {
    const reader = new FileReader();
    reader.onload = () => {
      this.base64Image = reader.result as string;
      if (this.isEdit) {
        this.addPortfolioForm.markAsDirty();
      }
    };
    reader.onerror = () => {
      this.commonService.errorNotification('Unable to read the selected image');
    };
    reader.readAsDataURL(file);
    fileInput.value = '';
  }

  submitAddPortfolio(): void {
    if (!this.addPortfolioForm.valid) {
      this.addPortfolioForm.markAllAsTouched();
      return;
    }

    const payload = this.buildLegacyPortfolioPayload();
    this.submitted = true;

    if (this.isEdit && this.portfolioDetails?.id) {
      this.portfolioService.updatePortfolio(this.portfolioDetails.id, payload).subscribe({
        next: () => {
          this.commonService.successNotification('Portfolio company updated successfully');
          this.portfolioService.setRefreshPortfolio(true);
        },
        error: () => this.handleLegacyPortfolioSaveError(),
        complete: () => this.completeLegacyPortfolioSave(),
      });
      return;
    }

    this.portfolioService.addPortfolio(this.vehicleId, payload).subscribe({
      next: () => {
        this.commonService.successNotification('Portfolio company added successfully');
        this.portfolioService.setRefreshPortfolio(true);
      },
      error: () => this.handleLegacyPortfolioSaveError(),
      complete: () => {
        this.completeLegacyPortfolioSave();
        this.base64Image = '';
      },
    });
  }

  convertToISOString(dateString: string): string | undefined {
    if (dateString) {
      const date = new Date(dateString);
      return date.toISOString();
    }

    return undefined;
  }
  convertToCustomFormat(dateString: string): Date | undefined {
    if (dateString) {
      const date = new Date(dateString);
      return date;
    }

    return undefined;
  }

  closePopup(): void {
    this.portfolioService.closePortfolioDrawer();
    this.portfolioService.setRefreshPortfolio(false);
    this.addPortfolioForm.reset();
    this.base64Image = '';
    this.resetFileErrors();
  }

  private buildLegacyPortfolioPayload(): LegacyPortfolioCompanyPayload {
    return {
      name: String(this.addPortfolioForm.get('name')?.value ?? '').trim(),
      inPortfolioSince: this.formatPortfolioDate(
        this.addPortfolioForm.get('inPortfolioSince')?.value
      ),
      logo: this.base64Image || null,
    };
  }

  private formatPortfolioDate(value: unknown): string | Date | null {
    if (!value) {
      return null;
    }

    if (this.isFormattableDate(value)) {
      return value.format('MM-DD-YYYY');
    }

    return value instanceof Date || typeof value === 'string' ? value : null;
  }

  private isFormattableDate(value: unknown): value is { format(format: string): string } {
    return typeof value === 'object'
      && value !== null
      && 'format' in value
      && typeof (value as { format?: unknown }).format === 'function';
  }

  private completeLegacyPortfolioSave(): void {
    this.submitted = false;
    this.portfolioService.closePortfolioDrawer();
    this.addPortfolioForm.reset();
  }

  private handleLegacyPortfolioSaveError(): void {
    this.submitted = false;
    this.commonService.errorNotification(
      'Something went wrong. Please inform your ACE support contact. Thank you!'
    );
  }

  private resetFileErrors(): void {
    this.fileValidationError = false;
    this.fileDimensionError = false;
    this.isFileSizeExceed = false;
  }
}

// delete dialog component
@Component({
  selector: 'app-delete-portfolio-dialog',
  templateUrl: './delete-portfolio-dialog.html',
  styleUrls: ['./portfolio.component.scss'],
})
export class DeletePortfolioDialogComponent {
  loading = false;
  constructor(
    public dialogRef: MatDialogRef<DeletePortfolioDialogComponent>,
    public portfolioService: PortfolioService,
    @Inject(MAT_DIALOG_DATA) public data: { portfolio: PortfolioCompanyTableRow }
  ) {}

  deletePortfolioCompany() {
    this.loading = true;
    this.dialogRef.disableClose = true;
    this.portfolioService.deletePortfolio(this.data.portfolio.id).subscribe({
      next: () => {
        this.dialogRef.close('success');
      },
      error: () => {
        this.dialogRef.close('error');
      },
      complete: () => {
        this.loading = false;
        this.dialogRef.disableClose = false;
      },
    });
  }
}

export { DeletePortfolioDialogComponent as DeletePortfolioDialog };
